'use client'

import * as React from 'react'
import { Clock, Search, X } from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'
import * as ReactDOM from 'react-dom'

import { InputGroup, InputGroupAddon, InputGroupButton, InputGroupInput, InputGroupText } from '@ui/web/components'
import { typographyVariants } from '@ui/web/lib'
import cn from '@config/tailwind/cn'

const STORAGE_KEY = 'pibo:recent-searches'
const MAX_RECENT = 5

function loadRecent(): string[] {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '[]') as string[]
  } catch {
    return []
  }
}

function saveRecent(searches: string[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(searches))
}

interface ContentSearchProps {
  value: string
  onChange: (value: string) => void
  onSubmit: (value: string) => void
  placeholder?: string
  className?: string
  variant?: 'inline' | 'modal'
  defaultOpen?: boolean
}

export function ContentSearch({
  value,
  onChange,
  onSubmit,
  placeholder = 'Cari buku, latihan, proyek',
  className,
  variant = 'inline',
  defaultOpen = false,
}: ContentSearchProps) {
  const [open, setOpen] = React.useState(defaultOpen)
  const [recentSearches, setRecentSearches] = React.useState<string[]>([])
  const containerRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    setRecentSearches(loadRecent())
  }, [])

  React.useEffect(() => {
    if (variant !== 'inline') return
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [variant])

  const handleSubmit = (val: string) => {
    if (!val.trim()) return
    const updated = [val, ...recentSearches.filter((s) => s !== val)].slice(0, MAX_RECENT)
    setRecentSearches(updated)
    saveRecent(updated)
    onSubmit(val)
    setOpen(false)
  }

  const darkTrigger = (onClick: () => void) => (
    <motion.div
      key='trigger'
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.15 }}
      onClick={onClick}
      className={cn(
        'srd:flex srd:h-12 srd:w-full srd:items-center srd:gap-2 srd:px-3 srd:rounded-full srd:cursor-pointer',
        'srd:bg-grey-100/15',
      )}
    >
      <Search className='srd:size-6 srd:text-grey-100 srd:shrink-0' />
      <span className={cn('srd:flex-1 srd:text-grey-100', typographyVariants({ variant: 'body-lg' }))}>
        {value || placeholder}
      </span>
    </motion.div>
  )

  const filteredRecent = value.trim()
    ? recentSearches.filter((s) => s.toLowerCase().includes(value.toLowerCase()))
    : recentSearches

  const recentItems = (onMouseDownItem?: (e: React.MouseEvent) => void) =>
    filteredRecent.map((search, i) => (
      <button
        key={i}
        type='button'
        onMouseDown={onMouseDownItem}
        onClick={() => {
          onChange(search)
          handleSubmit(search)
        }}
        className={cn(
          'srd:w-full srd:flex srd:items-center srd:gap-x-3 srd:px-4 srd:py-3',
          'srd:text-left srd:hover:bg-grey-10 srd:transition-colors',
          typographyVariants({ variant: 'body-lg' }),
        )}
      >
        <Clock className='srd:size-6 srd:text-grey-40 srd:shrink-0' />
        <span className='srd:text-neutral-black'>{search}</span>
      </button>
    ))

  if (variant === 'inline') {
    return (
      <div ref={containerRef} className={cn('srd:relative', className)}>
        <AnimatePresence mode='wait' initial={false}>
          {!open ? (
            darkTrigger(() => setOpen(true))
          ) : (
            <motion.div
              key='input'
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
            >
              <InputGroup className='srd:rounded-full'>
                <InputGroupAddon align='inline-start'>
                  <InputGroupText>
                    <Search />
                  </InputGroupText>
                </InputGroupAddon>

                <InputGroupInput
                  value={value}
                  onChange={(e) => onChange(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSubmit(value)
                    if (e.key === 'Escape') setOpen(false)
                  }}
                  placeholder={placeholder}
                />

                {value && (
                  <InputGroupAddon align='inline-end'>
                    <InputGroupButton onMouseDown={(e) => e.preventDefault()} onClick={() => onChange('')}>
                      <X />
                    </InputGroupButton>
                  </InputGroupAddon>
                )}
              </InputGroup>
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {open && recentSearches.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.15, ease: 'easeOut' }}
              className={cn(
                'srd:absolute srd:top-full srd:left-0 srd:right-0 srd:z-50 srd:mt-1',
                'srd:bg-neutral-white srd:rounded-xl srd:border srd:border-grey-20 srd:shadow-md srd:overflow-hidden',
              )}
            >
              {recentItems((e) => e.preventDefault())}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    )
  }

  const modal =
    typeof document !== 'undefined'
      ? ReactDOM.createPortal(
          <AnimatePresence>
            {open && (
              <>
                <motion.div
                  key='overlay'
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className='srd:fixed srd:inset-0 srd:z-50 srd:bg-black/40'
                  onMouseDown={() => setOpen(false)}
                />
                <motion.div
                  key='dialog'
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.2, ease: 'easeOut' }}
                  className='srd:fixed srd:inset-0 srd:z-50 srd:flex srd:items-center srd:justify-center srd:pointer-events-none'
                >
                  <div
                    className='srd:w-full srd:max-w-lg srd:mx-4 srd:bg-neutral-white srd:rounded-2xl srd:shadow-xl srd:overflow-hidden srd:pointer-events-auto'
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    <InputGroup className='srd:rounded-none srd:border-t-0 srd:border-x-0'>
                      <InputGroupAddon align='inline-start'>
                        <InputGroupText>
                          <Search />
                        </InputGroupText>
                      </InputGroupAddon>

                      <InputGroupInput
                        autoFocus
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleSubmit(value)
                          if (e.key === 'Escape') setOpen(false)
                        }}
                        placeholder={placeholder}
                      />

                      <InputGroupAddon align='inline-end'>
                        <InputGroupButton
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => (value ? onChange('') : setOpen(false))}
                        >
                          <X />
                        </InputGroupButton>
                      </InputGroupAddon>
                    </InputGroup>

                    {recentSearches.length > 0 && (
                      <div className='srd:border-t srd:border-grey-20'>{recentItems()}</div>
                    )}
                  </div>
                </motion.div>
              </>
            )}
          </AnimatePresence>,
          document.body,
        )
      : null

  return (
    <div className={className}>
      {darkTrigger(() => setOpen(true))}
      {modal}
    </div>
  )
}
